import { ENTITY_MENTION_TYPES, EntityMentionType } from './llm-chat.js';

/**
 * A reference lives inside the message content as `[[ref:<type>:<id>|<name>]]`, mirroring
 * the `[[attachment:<id>]]` token the chat transcript already parses. Keeping identity and
 * name in the text means no extra column, no character offsets to keep in sync, and a
 * readable fallback once the record is deleted.
 *
 * The token carries the bare name, never the translated `Type: name` display text - the
 * prefix is a frontend concern, so the backend needs no translations.
 */
export interface ParsedEntityReference {
  type: EntityMentionType;
  id: string;
  /** Name captured when the message was sent. A fallback only - live display prefers the current name. */
  name: string;
  /** Offsets of the whole token within the content it was parsed from. */
  start: number;
  end: number;
  /** The matched token, verbatim. */
  raw: string;
}

/** Ids are KSUID/UUID-shaped, so they cannot contain the delimiters. */
const ID_PATTERN = '[A-Za-z0-9_-]+';

function tokenPattern(): RegExp {
  return new RegExp(
    `\\[\\[ref:(${ENTITY_MENTION_TYPES.join('|')}):(${ID_PATTERN})\\|((?:\\\\.|[^\\\\\\]])*)\\]\\]`,
    'g',
  );
}

/** `]` and `\` would end or corrupt the token, so both are escaped in the name. */
function escapeName(name: string): string {
  return name.replace(/\\/g, '\\\\').replace(/\]/g, '\\]');
}

function unescapeName(name: string): string {
  return name.replace(/\\(.)/g, '$1');
}

export function encodeEntityReferenceToken(
  type: EntityMentionType,
  id: string,
  name: string,
): string {
  return `[[ref:${type}:${id}|${escapeName(name.replace(/\s+/g, ' ').trim())}]]`;
}

export function parseEntityReferenceTokens(
  content: string | null | undefined,
): ParsedEntityReference[] {
  if (!content) return [];

  const found: ParsedEntityReference[] = [];
  for (const match of content.matchAll(tokenPattern())) {
    const start = match.index ?? 0;
    found.push({
      type: match[1] as EntityMentionType,
      id: match[2],
      name: unescapeName(match[3]),
      start,
      end: start + match[0].length,
      raw: match[0],
    });
  }
  return found;
}

/** Replaces every token with its captured name - for session titles and other plain-text uses. */
export function stripEntityReferenceTokensToNames(content: string | null | undefined): string {
  if (!content) return '';
  return content.replace(tokenPattern(), (_full, _type, _id, name: string) =>
    unescapeName(name),
  );
}

/** Distinct `type:id` pairs across several messages, in first-seen order. */
export function collectEntityReferenceKeys(
  contents: Array<string | null | undefined>,
): Array<{ type: EntityMentionType; id: string; name: string }> {
  const seen = new Map<string, { type: EntityMentionType; id: string; name: string }>();
  for (const content of contents) {
    for (const ref of parseEntityReferenceTokens(content)) {
      const key = buildEntityReferenceKey(ref.type, ref.id);
      if (!seen.has(key)) seen.set(key, { type: ref.type, id: ref.id, name: ref.name });
    }
  }
  return [...seen.values()];
}

export function buildEntityReferenceKey(type: EntityMentionType, id: string): string {
  return `${type}:${id}`;
}
